import {
  EventOutlined,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  NavigateBefore,
  NavigateNext,
  SkipPreviousOutlined,
} from '@mui/icons-material';
import { Button, IconButton, Popover, Tooltip } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useRef, useState } from 'react';
import { useTherapyPlanning } from './therapyPlanningProvider';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { therapyPlanningTestId } from './test-ids';

const ButtonStyle = {
  width: '32px',
  height: '32px',
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid #F1F1F1',
  background: '#FFFFFF',
};

const IconStyle = {
  color: '#6366F1',
  fontSize: '14px',
};

const BasicDateCalendar = ({ handleChangeDate, value, futureDisable = false }: any) => {
  const jsDate = moment(value, 'DD-MM-YYYY').toDate();
  return (
    <DateCalendar
      sx={{ width: '280px' }}
      defaultValue={jsDate}
      onChange={handleChangeDate}
      disableFuture={futureDisable}
    />
  );
};

const TherapyPlaningNavigation = () => {
  const { weekStartDate, setWeekStartDate, setWeekEndDate, weekEndDate } = useTherapyPlanning();

  const [selectedDate, setSelectedDate] = useState<any>(new Date());
  const calendarButtonRef = useRef<HTMLButtonElement | null>(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLButtonElement | null>(null);
  const openCalender = Boolean(calendarAnchorEl);

  const selectedPatient = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);

  const goToNextWeek = () => {
    setWeekStartDate(weekStartDate.clone().add(9, 'days'));
    setWeekEndDate(weekEndDate.clone().add(9, 'days'));
  };

  const goToPreviousWeek = () => {
    setWeekStartDate(weekStartDate.clone().subtract(9, 'days'));
    setWeekEndDate(weekEndDate.clone().subtract(9, 'days'));
  };

  const goToPreviousDay = () => {
    setWeekStartDate(weekStartDate.clone().subtract(1, 'days'));
    setWeekEndDate(weekEndDate.clone().subtract(1, 'days'));
  };
  const goToNextDate = () => {
    setWeekStartDate(weekStartDate.clone().add(1, 'days'));
    setWeekEndDate(weekEndDate.clone().add(1, 'days'));
  };

  const navigateToday = () => {
    setWeekStartDate(moment().clone().startOf('week'));
    setWeekEndDate(moment().clone().endOf('week'));
  };

  const navigateToEoc = () => {
    setWeekStartDate(
      moment(selectedPatient?.eocStartDate)
        .clone()
        .startOf('week')
    );
    setWeekEndDate(
      moment(selectedPatient?.eocStartDate)
        .clone()
        .endOf('week')
    );
  };
  const handleClickCalender = () => {
    setCalendarAnchorEl((prev) => (prev ? null : calendarButtonRef.current));
  };
  const handleCloseCalender = () => {
    setCalendarAnchorEl(null);
  };
  const handleChangeDate = (e: any) => {
    const newDate = moment(e);
    setSelectedDate(newDate.toDate());
    setWeekStartDate(newDate);
    setWeekEndDate(newDate.clone().add(9, 'days'));
    handleCloseCalender();
  };

  const today = moment();

  return (
    <Stack
      gap=".35rem"
      alignItems="center"
      flexDirection="row"
    >
      <Tooltip
        placement="top-start"
        title="Start of EoC"
      >
        <IconButton
          onClick={navigateToEoc}
          data-testid={therapyPlanningTestId('nav-eoc-start')}
          sx={ButtonStyle}
        >
          <SkipPreviousOutlined sx={IconStyle} />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement="top-start"
        title="Previous"
      >
        <IconButton
          onClick={goToPreviousWeek}
          data-testid={therapyPlanningTestId('nav-previous-window')}
          sx={ButtonStyle}
        >
          <KeyboardDoubleArrowLeft sx={IconStyle} />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement="top-start"
        title="Previous Day"
      >
        <IconButton
          onClick={goToPreviousDay}
          data-testid={therapyPlanningTestId('nav-previous-day')}
          sx={ButtonStyle}
        >
          <NavigateBefore sx={IconStyle} />
        </IconButton>
      </Tooltip>
      <Button
        variant="outlined"
        size="small"
        onClick={navigateToday}
        data-testid={therapyPlanningTestId('nav-today')}
        disabled={
          today.isAfter(weekStartDate) && today.isBefore(weekEndDate.clone().add(2, 'days'))
        }
      >
        Today
      </Button>
      <Tooltip
        placement="top-start"
        title="Next Day"
      >
        <IconButton
          onClick={goToNextDate}
          data-testid={therapyPlanningTestId('nav-next-day')}
          sx={ButtonStyle}
        >
          <NavigateNext sx={IconStyle} />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement="top-start"
        title="Next"
      >
        <IconButton
          onClick={goToNextWeek}
          data-testid={therapyPlanningTestId('nav-next-window')}
          sx={ButtonStyle}
        >
          <KeyboardDoubleArrowRight sx={IconStyle} />
        </IconButton>
      </Tooltip>
      <Tooltip
        placement="top-start"
        title="Select a date"
      >
        <IconButton
          ref={calendarButtonRef}
          onClick={handleClickCalender}
          data-testid={therapyPlanningTestId('nav-calendar')}
          sx={ButtonStyle}
        >
          <EventOutlined sx={IconStyle} />
        </IconButton>
      </Tooltip>
      <Popover
        open={openCalender}
        anchorEl={calendarAnchorEl}
        onClose={handleCloseCalender}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)' } } }}
      >
        <BasicDateCalendar
          value={selectedDate}
          handleChangeDate={handleChangeDate}
        />
      </Popover>
    </Stack>
  );
};

export default TherapyPlaningNavigation;